import {EnkelGruppeModell, GruppeMedlemListeModell, Medlemstatus, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {ChangeDetectionStrategy, Component, computed, input, signal, ViewChild} from "@angular/core";
import {MedlemInformasjonKomponent} from "web-ng2/moduler/medlem/komponenter/medlem-informasjon/medlem-informasjon";
import {KortHandlingerKomponent} from "web-ng2/delt/komponenter/kort/kort-handlinger/kort-handlinger";
import {MatButton} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {muligeTilgangsrollerForMedlem, TilgangsrollerMedlemInfo} from "felles-ng2/modell/medlemModellHjelpere";
import {KortKomponent} from "web-ng2/delt/komponenter/kort/kort";
import {RammeInnholdKomponent, RammeMedTittelKomponent} from "web-ng2/delt/komponenter/ramme-med-tittel/ramme-med-tittel";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {MatTooltip} from "@angular/material/tooltip";
import {MedlemRedigerKomponent} from "web-ng2/moduler/medlem/komponenter/medlem-rediger/medlem-rediger";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MedlemStengTilgangDialogKomponent} from "web-ng2/moduler/medlem/modale/medlem-stengtilgang-dialog/medlem-stengtilgang-dialog";
import {MatDialog} from "@angular/material/dialog";
import {MedlemGjenåpneTilgangDialogKomponent} from "web-ng2/moduler/medlem/modale/medlem-gjenåpnetilgang-dialog/medlem-gjenåpnetilgang-dialog";
import {MedlemAnonymiserDialogKomponent} from "web-ng2/moduler/medlem/modale/medlem-anonymiser-dialog/medlem-anonymiser-dialog";
import {MatIcon} from "@angular/material/icon";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {FormaterDatoPeriodeUtenTidPipe} from "felles-ng2/pipes/FormaterDatoPeriodeUtenTidPipe";

const debug = initDebugger('MedlemKortKomponent');

@Component({
    selector: 'medlem-kort',
    templateUrl: 'medlem-kort.html',
    styleUrl: 'medlem-kort.scss',
    host: { 'class': 'e2e-medlem' },
    imports: [
        MatTooltip,
        MatButton,
        MatIcon,
        TranslateModule,
        FormaterDatoPeriodeUtenTidPipe,
        KortKomponent,
        MedlemInformasjonKomponent,
        KortHandlingerKomponent,
        TipsKomponent,
        RammeMedTittelKomponent,
        MedlemRedigerKomponent,
        RammeInnholdKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemKortKomponent {

    /** kort-kontrolleren - merk at denne ikke er satt initielt, kan altså være undefined */
    @ViewChild(KortKomponent) kortKomp!: KortKomponent;

    medlem = input.required<GruppeMedlemListeModell>();
    gruppe = input.required<EnkelGruppeModell>();

    tilgangsroller = computed(() => tilgangsrollerInfo(this.medlem()));

    erRedigeringsmodus = signal(false);
    visInaktiveMedlemsroller = signal(false);

    redigeringMulig = computed(() => this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.gruppe().id));
    visRedigerKnapp = computed(() => this.redigeringMulig() && this.medlem()?.harTilgang);
    visStengTilgangKnapp = computed(() => this.redigeringMulig() && this.medlem()?.harTilgang);
    visGjenaapneTilgangKnapp = computed(() => this.redigeringMulig() && !this.medlem().harTilgang);
    erInnloggetBruker = computed(() => this.kontekst.autentisertBruker().bruker.id == this.medlem().brukerId);

    readonly medlemstatuser = Medlemstatus;

    constructor(private navigasjon: Navigasjon, private navigasjonDeaktivering: NavigasjonDeaktivering, private kontekst: BrukerkontekstTilbyder, public enumOversettelser: EnumOversettelser, private matDialog: MatDialog) {
    }

    visMer() {
        this.kortKomp.setSomAktiv(); //navigasjon.gåTilMedlem() vil sørge for å scrolle til kortet - kaller setSomAktiv() her mest for at visning av ekspandert kort skal fungere i storybook
        this.navigasjon.gåTilMedlem(this.gruppe().id, this.medlem().id);
    }

    visMindre() {
        this.kortKomp.setSomIkkeAktiv();
    }

    rediger() {
        this.navigasjonDeaktivering.deaktiverNavigasjon();
        this.erRedigeringsmodus.set(true);
    }

    vedRedigeringFerdig() {
        this.navigasjonDeaktivering.reaktiverNavigasjon();
        this.erRedigeringsmodus.set(false);
        //redigering kan ha ført til at rekkefølgen har blitt endret..
        //må skje med timeout da this.kortKomp kan være null
        setTimeout(() => this.kortKomp?.skrollTil(), 500)
    }

    stengTilgang() {
        MedlemStengTilgangDialogKomponent.vis(this.matDialog, this.medlem()!, this.gruppe()!);
    }

    gjenaapneTilgang() {
        MedlemGjenåpneTilgangDialogKomponent.vis(this.matDialog, this.medlem()!);
    }

    anonymiser() {
        MedlemAnonymiserDialogKomponent.vis(this.matDialog, this.medlem()!, this.gruppe()!);
    }

    gaaTilMoetemal() {
        this.navigasjon.gåTilGruppeMøtemal(this.gruppe().id);
    }
}

function tilgangsrollerInfo(medlem: GruppeMedlemListeModell | null): TilgangsrollerMedlemInfo[] {
    return erNil(medlem) ? [] :
      muligeTilgangsrollerForMedlem()
        .filter(rolleInfo => medlem.tilgangsroller.includes(rolleInfo.rolle))
}
